import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, ApiError, type MetaAssets, type OnboardingStatus } from '../api';

const checklist = [
  { key: 'account', title: 'Account created', detail: 'Your workspace and secure session are ready.', href: '/app' },
  { key: 'meta', title: 'Connect Meta Ads', detail: 'Add an ad account, Page, Pixel and token with ads_management.', href: '/app/channels' },
  { key: 'products', title: 'Import and validate products', detail: 'Products need a title and a positive price. Images are strongly recommended.', href: '/app/products' },
  { key: 'pixel', title: 'Verify tracking with real events', detail: 'Load your site for a page view, then complete a test order to verify revenue.', href: '/app/pixel' },
  { key: 'campaign', title: 'Build your first safe campaign', detail: 'Start with Meta, one proven product and the minimum daily budget.', href: '/app/campaigns/new' },
] as const;

export default function Onboarding() {
  const [status, setStatus] = useState<OnboardingStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [metaAssets, setMetaAssets] = useState<MetaAssets | null>(null);
  const [adAccountId, setAdAccountId] = useState('');
  const [pageId, setPageId] = useState('');
  const [pixelId, setPixelId] = useState('');
  const [search] = useSearchParams();

  const load = useCallback(() => {
    api.onboardingStatus().then(setStatus).catch((err) =>
      setError(err instanceof Error ? err.message : 'Could not load onboarding status.'),
    );
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, 10_000);
    return () => clearInterval(timer);
  }, [load]);

  useEffect(() => {
    const state = search.get('meta_state');
    if (!state) return;
    api.metaAssets(state).then((assets) => {
      setMetaAssets(assets);
      setAdAccountId(assets.adAccounts[0]?.id ?? '');
      setPageId(assets.pages[0]?.id ?? '');
      const firstAccount = assets.adAccounts[0]?.id;
      setPixelId(assets.pixels.find((pixel) => pixel.adAccountId === firstAccount)?.id ?? '');
    }).catch((err) => setError(err instanceof Error ? err.message : 'Could not load Meta assets.'));
  }, [search]);

  async function connectMeta() {
    setBusy('meta');
    setError(null);
    try {
      const { url } = await api.startMetaOAuth();
      window.location.assign(url);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not start Meta connection.');
      setBusy(null);
    }
  }

  async function saveMetaAssets() {
    const state = search.get('meta_state');
    if (!state) return;
    setBusy('meta-select');
    try {
      await api.selectMetaAssets(state, { adAccountId, pageId, pixelId });
      setMetaAssets(null);
      window.history.replaceState({}, '', '/app/onboarding?meta=connected');
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save Meta assets.');
    } finally {
      setBusy(null);
    }
  }

  async function buy(packageName: 'guided' | 'done_for_you') {
    setBusy(packageName);
    setError(null);
    try {
      const result = await api.supportCheckout(packageName);
      window.location.assign(result.url);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not start checkout.');
      setBusy(null);
    }
  }

  if (!status && !error) return <div className="empty">Checking your setup…</div>;

  return (
    <>
      <p className="eyebrow">Self-service setup</p>
      <h1 className="pg-title">Launch with confidence</h1>
      <p className="pg-sub">
        This checklist reads your real account state. It only marks tracking complete after a
        purchase event arrives, and only marks Meta complete after credentials are stored.
      </p>

      {search.get('support') === 'paid' && <p className="ok-line">Payment received. Check your email for the booking link.</p>}
      {search.get('support') === 'cancelled' && <p className="warn-line">Checkout cancelled. Your self-service setup is still available.</p>}
      {search.get('meta') === 'connected' && <p className="ok-line">Meta connected securely. Your selected assets are ready.</p>}
      {search.get('meta_error') && <p className="err-line">Meta did not authorise the connection: {search.get('meta_error')}. Try again and grant every requested permission.</p>}
      {error && <p className="err-line">{error}</p>}

      {metaAssets && (
        <div className="card card-p" style={{ marginBottom: 20 }}>
          <h3>Select the assets ScaleAds AI should use</h3>
          {metaAssets.adAccounts.length === 0 || metaAssets.pages.length === 0 ? (
            <p className="warn-line">Meta returned no usable ad accounts or Pages. Confirm your Facebook user has administrator access, then reconnect.</p>
          ) : (
            <>
              <div className="field"><label>Ad account</label><select value={adAccountId} onChange={(event) => {
                const id = event.target.value; setAdAccountId(id);
                setPixelId(metaAssets.pixels.find((pixel) => pixel.adAccountId === id)?.id ?? '');
              }}>{metaAssets.adAccounts.map((asset) => <option key={asset.id} value={asset.id}>{asset.name} · {asset.id}</option>)}</select></div>
              <div className="field"><label>Facebook Page</label><select value={pageId} onChange={(event) => setPageId(event.target.value)}>{metaAssets.pages.map((asset) => <option key={asset.id} value={asset.id}>{asset.name}</option>)}</select></div>
              <div className="field"><label>Meta Pixel</label><select value={pixelId} onChange={(event) => setPixelId(event.target.value)}>{metaAssets.pixels.filter((asset) => asset.adAccountId === adAccountId).map((asset) => <option key={asset.id} value={asset.id}>{asset.name}</option>)}</select></div>
              <button className="btn brand" disabled={!adAccountId || !pageId || !pixelId || busy !== null} onClick={() => void saveMetaAssets()}>{busy === 'meta-select' ? 'Saving…' : 'Use these Meta assets'}</button>
            </>
          )}
        </div>
      )}

      <div className={`card card-p setup-status ${status?.ready ? 'done' : ''}`}>
        <h4>{status?.ready ? 'Ready to launch' : 'Setup in progress'} <span className="meta">refreshes every 10 seconds</span></h4>
        <ol className="setup-steps">
          {checklist.map((item, index) => {
            const step = status?.steps[item.key];
            const done = Boolean(step?.complete);
            return (
              <li key={item.key} className={done ? 'done' : 'active'}>
                <span className="tick">{done ? '✓' : index + 1}</span>
                <div style={{ flex: 1 }}><strong>{item.title}</strong><em>{item.detail}</em></div>
                <Link className="btn sm" to={item.href}>{done ? 'Review' : 'Continue'}</Link>
              </li>
            );
          })}
        </ol>
        {status && status.blockers.length > 0 && (
          <div className="warn-line">
            <strong>Before launch:</strong>
            <ul>{status.blockers.map((blocker) => <li key={blocker}>{blocker}</li>)}</ul>
          </div>
        )}
      </div>

      {!status?.steps.meta.complete && !metaAssets && (
        <button className="btn brand" style={{ marginTop: 14 }} disabled={busy !== null} onClick={() => void connectMeta()}>
          {busy === 'meta' ? 'Opening Meta…' : 'Connect Meta securely'}
        </button>
      )}

      <h2 className="pg-title" style={{ fontSize: 22, marginTop: 32 }}>Want help?</h2>
      <p className="pg-sub">Self-service onboarding is included. Pay only if you want a person to help.</p>
      <div className="channel-grid">
        <article className="card card-p channel">
          <h3>Guided onboarding · A$149</h3>
          <p className="channel-edge">A live setup session, account connection help and a review of your first campaign.</p>
          <button className="btn brand" disabled={!status?.support.checkoutConfigured || busy !== null} onClick={() => void buy('guided')}>
            {busy === 'guided' ? 'Opening checkout…' : 'Book guided onboarding'}
          </button>
        </article>
        <article className="card card-p channel">
          <h3>Done-for-you launch · A$399</h3>
          <p className="channel-edge">We configure connections, validate tracking and prepare your first campaign for approval.</p>
          <button className="btn brand" disabled={!status?.support.checkoutConfigured || busy !== null} onClick={() => void buy('done_for_you')}>
            {busy === 'done_for_you' ? 'Opening checkout…' : 'Choose done-for-you'}
          </button>
        </article>
      </div>
      {!status?.support.checkoutConfigured && (
        <p className="footnote">Assisted onboarding checkout will appear when its Stripe prices are configured. Self-service setup remains available.</p>
      )}
    </>
  );
}
